import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { Button } from '@deepguide-ai/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog';
import { queryClient, trpc } from '@/lib/trpc';
import { vendorLogoPath } from '@/lib/vendor-logo';
import type { ConfigModalProps, IntegrationProvider } from '../types';
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle';

function ZoomConfigModal({ open, onOpenChange, integration, workspaceId, onComplete }: ConfigModalProps) {
  const [connecting, setConnecting] = useState(false);
  const integrationId = integration?.id ?? '';
  const status = useQuery({ ...trpc.integrations.getZoomConnectionStatus.queryOptions({ integrationId }), enabled: open && !!integrationId });
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='sm:max-w-xl'>
      <DialogHeader>
        <DialogTitle>Zoom evidence</DialogTitle>
        <DialogDescription>Collect account access, authentication settings and operation logs for evidence review.</DialogDescription>
      </DialogHeader>
      <ScrollArea type='scroll' className='max-h-[65vh]' viewClassName='max-h-[65vh] [&>div]:!block'>
      <div className='space-y-3 px-6 pb-4 text-sm'>
        {integrationId && status.isLoading ? <p role='status'>Loading Zoom connection…</p> : null}
        {status.error ? <div className='space-y-2'>
          <p role='alert' className='text-destructive'>{status.error.message}</p>
          <Button variant='outline' disabled={status.isFetching} onClick={() => void status.refetch()}>Retry loading</Button>
        </div> : status.data ? <dl className='space-y-1'><div><dt className='text-muted-foreground'>Connected account</dt><dd>{status.data.providerAccount}</dd></div></dl> : null}
        <p className='text-muted-foreground'>Use Scan on the Zoom card to collect evidence and view progress and results. Use Access reviews to review collected accounts. Connecting alone does not collect evidence.</p>
        <p className='text-muted-foreground'>Inventory includes active, deactivated and pending accounts, common roles and direct groups. Invitations without user IDs remain separate evidence. Scoped roles and unresolved members need review. MFA enrollment is unavailable; MFA and SSO settings do not prove effective protection.</p>
        <p className='text-muted-foreground'>Operation logs cover the last 28 calendar dates and require a Pro or higher plan. Free-text operation details are omitted. Collected logs do not establish full audit-period coverage, retention or human review. Meeting recordings are not collected by these checks.</p>
        <p className='text-muted-foreground'>A platform operator must configure the account-level Zoom OAuth app before connecting. Role, group and security reads require the relevant plan and permissions. Existing connections need reconnecting after the app's operation-log read permission is approved.</p>
        {status.data?.lastError ? <p role='alert' className='text-destructive'>{status.data.lastError}</p> : null}
        <p className='text-muted-foreground'>Disconnect from the integration card to stop future collection. Collected evidence is retained. Revoke app access in Zoom separately.</p>
      </div>
      </ScrollArea>
      <DialogFooter>
        <Button disabled={connecting} onClick={async () => {
          setConnecting(true);
          try { await ZoomProvider.handleConnect({ workspaceId, onSuccess: onComplete, onFinish: () => setConnecting(false) }); }
          catch { setConnecting(false); }
        }}>{connecting ? 'Connecting…' : integrationId ? 'Reconnect Zoom' : 'Connect Zoom'}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

export const ZoomProvider: IntegrationProvider = {
  id: 'Zoom', name: 'Zoom', description: 'Account users, roles, groups, authentication settings and operation logs',
  Icon: () => <img src={vendorLogoPath('zoom')} alt='' className='size-6 object-contain' />,
  ConfigModal: ZoomConfigModal, postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    { id: 'authentication', name: 'Authentication settings', description: 'Read account MFA policy, sign-in methods and SSO configuration', enabled: true },
    { id: 'access', name: 'Access inventory', description: 'Read users, common roles, assignments and direct groups', enabled: true },
    { id: 'audit', name: 'Operation logs', description: 'Read a bounded 28-date window of administrative activity', enabled: true },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.initZoomAuth.mutationOptions()).execute(undefined).catch(error => {
      toast.error(error instanceof Error ? error.message : 'Could not start Zoom authorization');
      onFinish?.('error');
      throw error;
    });
    openOAuthConnectPopup({ authUrl: result.authUrl, windowName: 'zoom-auth', provider: 'Zoom', onSuccess, onFinish,
      onError: message => toast.error(message), onPopupBlocked: () => toast.error('Allow popups to connect Zoom.'),
    });
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient.getMutationCache().build(queryClient, trpc.integrations.completeZoomAuth.mutationOptions()).execute({ code, state });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.integrations.getZoomConnectionStatus.queryKey() }),
    ]);
    return result;
  },
  syncData: async () => { throw new Error('Use Scan on the Zoom integration card to collect access evidence.'); },
};
